import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormToggle from "../../Form/FormToggle";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearBusinessTax,
  clearBusinessTaxError,
  requestCreateBusinessTax,
  requestListDebitAccount,
  requestUpdateBusinessTax,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useNotificationDispatch,
} from "../../../../Context";
import { useTranslation } from "react-i18next";
import {
  purchaseAccountCategories,
  salesAccountCategories,
  TAX_PERCENTAGE_MAX,
  TAX_PERCENTAGE_MIN,
  resolveDefaultTaxAccount,
  taxComputationOptions,
  taxKindOptions,
  taxTypesOptions,
  WITHHOLDING_BY_KIND,
} from "../../../../Constants";
import { cancelAPIRequest } from "../../../../Utils";

// The input shows "1,500,000" for FIXED and "-2" or "1.5" for PERCENTAGE.
// Drop only the thousand separators so the sign and decimals survive.
const parseAmount = (value) => Number(String(value).replace(/,/g, ""));

// `kind` is optional, and FormSelect has no clear affordance of its own — with
// no way back to "unset" the field would be write-once on an existing tax.
const NO_KIND = { value: null, label: "common.none" };
const kindOptions = [NO_KIND, ...taxKindOptions];

const TaxModal = ({ open, onClose, tax }) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [type, setType] = useState(taxTypesOptions[0]);
  const [computation, setComputation] = useState(taxComputationOptions[1]);
  const [kind, setKind] = useState(NO_KIND);
  const [withholding, setWithholding] = useState(false);
  const [amount, setAmount] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [errors, setErrors] = useState({});

  const notificationDispatch = useNotificationDispatch();
  const accountingDispatch = useAccountingDispatch();
  const taxDispatch = useBusinessTaxDispatch();

  const { debitLoading, debits, debitError } = useAccountingState();
  const { user, getUserLoading: userLoading } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    request,
    newTax,
    newTaxLoading,
    newTaxError,
    taxID: editTax,
    updateTaxLoading: editTaxLoading,
    updateTaxError: editTaxError,
  } = useBusinessTaxState();

  const isEdit = Boolean(tax);

  const submitLoading = userLoading || newTaxLoading || editTaxLoading;

  const debitOptions = useMemo(() => {
    return debits
      .slice()
      .sort((a, b) => {
        const aCode = parseInt(a.code, 10);
        const bCode = parseInt(b.code, 10);
        return (aCode < 100000) - (bCode < 100000) || aCode - bCode;
      })
      .map((obj) => ({
        ...obj,
        value: obj.id,
        label: `${obj.code} - ${obj.name}`,
      }));
  }, [debits]);

  // Clean up on unmount only. With `request` as a dependency this cleanup also
  // ran when a failed save reset `request` to null, wiping the error before it
  // was shown.
  const requestRef = useRef(request);
  requestRef.current = request;

  // Once the user has spoken for either field, the kind must stop deciding it
  // for them. Reset when the modal is handed a different tax, not on every
  // render, or reopening on the same tax would forget the choice mid-edit.
  const withholdingTouchedRef = useRef(false);
  const accountTouchedRef = useRef(false);
  const suggestedForRef = useRef(null);
  useEffect(() => {
    withholdingTouchedRef.current = false;
    accountTouchedRef.current = false;
    suggestedForRef.current = null;
  }, [tax?.id, open]);
  useEffect(() => {
    return () => {
      cancelAPIRequest(requestRef.current);
      clearBusinessTax(taxDispatch);
      clearBusinessTaxError(taxDispatch);
    };
  }, [taxDispatch]);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      let accountCategories;

      if (type.value === "SALES") {
        accountCategories = salesAccountCategories;
      }

      if (type.value === "PURCHASES") {
        accountCategories = purchaseAccountCategories;
      }

      requestListDebitAccount(accountingDispatch, {
        businessID: user.mainBusiness.id,
        categories: accountCategories,
      });
    }
  }, [accountingDispatch, user?.mainBusiness?.id, type]);

  // The account a tax posts to follows from its kind and its direction: a PPN
  // on a sale is a liability, a PPh 23 on a sale is a prepaid-tax asset. Only
  // suggest one — an existing tax keeps the account it was saved with, which
  // this effect used to overwrite with 220500/110500 every time the modal
  // opened, silently reassigning the user's choice on the next save.
  useEffect(() => {
    if (!debitOptions.length) return;

    const toOption = (account) => ({
      ...account,
      label: `${account.code} - ${account.name}`,
      value: account.id,
    });

    // A saved tax opens on the account it was saved with.
    if (isEdit && tax?.accountID && !accountTouchedRef.current) {
      const stored = debitOptions.find((debit) => debit.id === tax.accountID);
      if (stored) {
        setSelectedAccount(toOption(stored));
        suggestedForRef.current = `${kind?.value}|${type?.value}`;
        return;
      }
    }

    // Re-suggest when the kind or the direction changes — those are what
    // decide the account — but never over an account the user chose while the
    // pair stayed the same, or a refetch of the account list would undo it.
    const pair = `${kind?.value}|${type?.value}`;
    if (accountTouchedRef.current && suggestedForRef.current === pair) return;
    suggestedForRef.current = pair;
    accountTouchedRef.current = false;

    // No suggestion means the kind has no sensible default (a final PPh 4(2)
    // on a sale) or this chart of accounts predates the code. Leave the picker
    // empty rather than parking the tax on an unrelated account.
    const suggested = resolveDefaultTaxAccount(
      debitOptions,
      kind?.value,
      type?.value,
    );
    setSelectedAccount(suggested ? toOption(suggested) : null);
  }, [debitOptions, isEdit, tax?.accountID, kind, type]);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && tax) {
      setName(tax.name);
      setType(taxTypesOptions.find((item) => item.value === tax.type));
      setComputation(
        taxComputationOptions.find((item) => item.value === tax.computation),
      );
      setAmount(tax.amount.toString());
      setKind(
        taxKindOptions.find((item) => item.value === tax.kind) || NO_KIND,
      );
      setWithholding(Boolean(tax.withholding));
    }
  }, [isEdit, tax]);

  // Picking a kind says what the tax is, which in turn says whether it is cut
  // by the counterparty — but only until the user says otherwise. An existing
  // tax keeps what it was saved with.
  const handleKindChange = (nextKind) => {
    setKind(nextKind);
    clearError("kind");
    if (!withholdingTouchedRef.current) {
      setWithholding(Boolean(WITHHOLDING_BY_KIND[nextKind?.value]));
      clearError("amount");
    }
  };

  const handleWithholdingChange = (next) => {
    withholdingTouchedRef.current = true;
    setWithholding(next);
    clearError("amount");
  };

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const validate = () => {
    const newErrors = {};

    if (!name) {
      newErrors.name = "taxes.errors.nameRequired";
    }

    if (!type?.value) {
      newErrors.type = "taxes.errors.typeRequired";
    }

    if (!computation?.value) {
      newErrors.computation = "taxes.errors.computationRequired";
    }
    const parsedAmount = parseAmount(amount);
    if (!amount || Number.isNaN(parsedAmount)) {
      newErrors.amount = "taxes.errors.amountRequired";
    } else if (
      computation?.value === "PERCENTAGE" &&
      (parsedAmount < TAX_PERCENTAGE_MIN || parsedAmount > TAX_PERCENTAGE_MAX)
    ) {
      newErrors.amount = "taxes.errors.percentageRange";
    } else if (withholding && parsedAmount < 0) {
      // The resolver rejects this too. A negative rate is how withholding was
      // entered before there was a flag for it; now the two together would
      // subtract the tax twice.
      newErrors.amount = "taxes.errors.withholdingNegative";
    }
    if (!selectedAccount?.value) {
      newErrors.selectedAccount = "taxes.errors.accountRequired";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveProduct = () => {
    const input = {
      businessID: user?.mainBusiness?.id,
      name,
      computation: computation?.value,
      type: type?.value,
      amount: parseAmount(amount),
      accountID: selectedAccount?.value,
      withholding,
    };

    if (isEdit) {
      input.id = tax?.id;
      // On update an omitted key keeps the stored value, so clearing the field
      // has to be sent as an explicit null.
      input.kind = kind?.value ?? null;
      requestUpdateBusinessTax(taxDispatch, { input });
    } else {
      if (kind?.value) input.kind = kind.value;
      requestCreateBusinessTax(taxDispatch, { input });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    saveProduct();
  };

  //  Handle create success
  useEffect(() => {
    if (newTax) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-product-${newTax}`,
        type: "success",
        title: "common.success",
        body: "taxes.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newTax, onClose]);

  //  Handle update success
  useEffect(() => {
    if (editTax) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-product-${editTax}`,
        type: "success",
        title: "common.success",
        body: "taxes.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, editTax, onClose]);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? "taxes.modal.title.edit" : "taxes.modal.title.add"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked || submitLoading}
            loading={submitLoading}
            type="submit"
            form="new-tax-form"
            variant="primary"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="new-tax-form" className="space-y-4" onSubmit={handleSubmit}>
        <WriteBlockGate>
        <FormError error={newTaxError || editTaxError} />
        <Fieldset
          disabled={submitLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <Field>
            <FormInput
              required
              label="taxes.fields.name"
              id="tax-name"
              name="tax_name"
              value={name}
              onChange={handleFieldChange(setName, "name")}
              onClear={() => setName("")}
              placeholder={t("taxes.placeholders.name")}
              error={errors.name}
            />
          </Field>

          <FormRadioGroup
            required
            label="taxes.fields.type"
            name="type"
            options={taxTypesOptions}
            value={type}
            onChange={handleFieldChange(setType, "type")}
            gridClassName="grid-cols-1 sm:grid-cols-2"
            error={errors.type}
          />

          <Field>
            <FormSelect
              required
              label="taxes.fields.computation"
              id="computation"
              name="computation"
              options={taxComputationOptions}
              value={computation}
              onChange={handleFieldChange(setComputation, "computation")}
              placeholder={t("taxes.placeholders.computation")}
              error={errors.computation}
            />
          </Field>

          <Field>
            <FormSelect
              label="taxes.fields.kind"
              id="kind"
              name="kind"
              hint={t("taxes.hints.kind")}
              options={kindOptions}
              value={kind}
              onChange={handleKindChange}
              placeholder={t("taxes.placeholders.kind")}
              error={errors.kind}
            />
          </Field>

          <Field>
            <FormToggle
              fieldLabel="taxes.fields.withholding"
              hint={t("taxes.hints.withholding")}
              label={t("taxes.fields.withholdingToggle")}
              checked={withholding}
              onChange={handleWithholdingChange}
            />
          </Field>

          <Field>
            <FormInput
              required
              label="taxes.fields.amount"
              id="amount"
              name="amount"
              value={amount}
              onChange={handleFieldChange(setAmount, "amount")}
              onClear={() => setAmount("")}
              placeholder={t("taxes.placeholders.amount")}
              numberFormat={{
                thousandSeparator: computation.value === "FIXED" ? true : false,
              }}
              prefix={computation.value === "FIXED" ? "Rp " : "%"}
              error={errors.amount}
            />
          </Field>

          <Field>
            <FormSelect
              required
              label="taxes.fields.account"
              id="account"
              name="account"
              searchable
              options={debitOptions}
              isLoading={debitLoading}
              value={selectedAccount}
              onChange={(next) => {
                accountTouchedRef.current = true;
                setSelectedAccount(next);
                clearError("selectedAccount");
              }}
              placeholder={t("taxes.placeholders.account")}
              error={errors.selectedAccount || debitError}
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default TaxModal;
